import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import Excel from "../../../icons/excel.svg";
import { getProveedoresBlando, getProveedoresBlandoExcel } from "../../../services/proveedor.service";
import { logVisitaMenu } from "../../../services/log.service";

const COLUMNS = [
  { key: "nif", label: "NIF", width: 110 },
  { key: "razonSocial", label: "Razón Social", width: 320, align: "left" },
  { key: "emailContacto", label: "Email contacto", width: 220, align: "left" },
  { key: "fechaAlta", label: "Fecha alta", width: 110 },
  { key: "estado", label: "Estado", width: 110 },
  { key: "porcentajeBlando", label: "Nuevo % válido", width: 120, pct: true },
  { key: "docsCaducados", label: "Nº docs caducados", width: 130 },
  { key: "ultimaReclamacion", label: "Última reclamación", width: 140 },
  { key: "numContactos", label: "Nº contactos", width: 110 },
  { key: "numComunidades", label: "Nº comunidades", width: 120 },
];

const PAGE_SIZE = 50;

export default function IndicadoresOperativa() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    logVisitaMenu("indicadores_operativa");
  }, []);

  useEffect(() => {
    let activo = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getProveedoresBlando();
        if (activo) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error cargando proveedores blando:", e);
        if (activo) toast.error("Error al cargar el informe de proveedores");
      } finally {
        if (activo) setLoading(false);
      }
    })();
    return () => { activo = false; };
  }, []);

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      String(r.razonSocial || "").toLowerCase().includes(q) ||
      String(r.nif || "").toLowerCase().includes(q) ||
      String(r.emailContacto || "").toLowerCase().includes(q)
    );
  }, [rows, busqueda]);

  useEffect(() => { setPage(1); }, [busqueda]);

  const totalPages = Math.max(1, Math.ceil(filtradas.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const visibles = filtradas.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const descargarExcel = async () => {
    if (descargando) return;
    setDescargando(true);
    const toastId = toast.loading("Generando Excel...");
    try {
      const blob = await getProveedoresBlandoExcel();
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `proveedores-blando-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.update(toastId, { render: "Excel descargado", type: "success", isLoading: false, autoClose: 3000 });
    } catch (e) {
      console.error("Error descargando Excel:", e);
      toast.update(toastId, { render: "Error al descargar el Excel", type: "error", isLoading: false, autoClose: 5000 });
    } finally {
      setDescargando(false);
    }
  };

  const fmt = (col, value) => {
    if (col.pct) return `${value ?? 0}%`;
    return value === null || value === undefined || value === "" ? "-" : value;
  };

  const pctColor = (v) => (v < 25 ? "#FF5252" : v < 75 ? "#e69500" : "#7bc977");

  return (
    <div style={{ padding: "16px 20px", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#92004D" }}>
          Proveedores — informe porcentajes
          {!loading && <span style={{ fontSize: 13, fontWeight: 400, color: "#888", marginLeft: 10 }}>({filtradas.length})</span>}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por razón social, NIF o email..."
            style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: 8, fontSize: 14, width: 280 }}
          />
          <button
            onClick={descargarExcel}
            disabled={descargando || loading}
            className="bigBtn"
            style={{ display: "flex", alignItems: "center", gap: 8, opacity: (descargando || loading) ? 0.6 : 1 }}
          >
            {descargando
              ? <CircularProgress style={{ color: "#fff", width: 16, height: 16 }} />
              : <img src={Excel} alt="Excel" width="22px" />}
            Descargar Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "#666", padding: "60px 0" }}>
          <CircularProgress style={{ color: "#92004D" }} />
          <span>Calculando informe de proveedores…</span>
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      style={{
                        background: "#92004D", color: "#fff",
                        padding: "10px 12px", textAlign: c.align || "center", whiteSpace: "nowrap",
                        minWidth: c.width,
                      }}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 ? (
                  <tr><td colSpan={COLUMNS.length} style={{ padding: 24, textAlign: "center", color: "#888" }}>No hay proveedores</td></tr>
                ) : (
                  visibles.map((r, i) => (
                    <tr key={r.id} style={{ background: i % 2 ? "#f7f8fa" : "#fff" }}>
                      {COLUMNS.map((c) => (
                        <td
                          key={c.key}
                          style={{
                            padding: "8px 12px", textAlign: c.align || "center",
                            borderBottom: "1px solid #eee", whiteSpace: c.align === "left" ? "normal" : "nowrap",
                            color: c.pct ? pctColor(r[c.key] ?? 0) : "#333",
                            fontWeight: c.pct ? 600 : 400,
                          }}
                        >
                          {fmt(c, r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 10, fontSize: 13, color: "#555" }}>
            <span>Página {pageSafe} de {totalPages}</span>
            <button className="bigBtn" style={{ padding: "4px 12px" }} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe <= 1}>‹</button>
            <button className="bigBtn" style={{ padding: "4px 12px" }} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages}>›</button>
          </div>
        </>
      )}
    </div>
  );
}
